/* eslint-disable react/no-unescaped-entities */
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-xl">Privacy Policy</h1>
              <p className="text-sm text-slate-400">Last updated: January 13, 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-3xl font-bold mb-6">SlideCon Privacy Policy</h1>
          
          <p className="text-slate-300 mb-6">
            SlideCon (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is committed to protecting your privacy. This 
            Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our Service. This policy complies with the California Consumer Privacy 
            Act (CCPA) and other applicable privacy laws.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">1. Information We Collect</h2>
          
          <h3 className="text-xl font-semibold mt-6 mb-3">Personal Information</h3>
          <p className="text-slate-300">We may collect the following personal information:</p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li><strong>Account Information:</strong> Name, email address, password, profile picture</li>
            <li><strong>Communication Data:</strong> Messages sent through our platform, community posts</li>
            <li><strong>Usage Data:</strong> How you interact with our Service, features used, time spent</li>
            <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
            <li><strong>Content Data:</strong> Presentations, documents, and media you upload or create</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Information Collected Automatically</h3>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Log data and analytics</li>
            <li>Cookies and similar tracking technologies</li>
            <li>Device identifiers</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="text-slate-300">We use collected information to:</p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Provide, maintain, and improve our Service</li>
            <li>Process your presentations and generate AI content</li>
            <li>Communicate with you about updates and features</li>
            <li>Ensure security and prevent fraud</li>
            <li>Comply with legal obligations</li>
            <li>Personalize your experience</li>
            <li>Analyze usage patterns to improve our Service</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">3. Information Sharing</h2>
          <p className="text-slate-300">We do not sell your personal information. We may share information with:</p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li><strong>Service Providers:</strong> Third parties that help us operate our Service (hosting, analytics, AI processing)</li>
            <li><strong>Community Members:</strong> When you participate in community features, other members may see your profile and posts</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
            <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">4. California Consumer Privacy Act (CCPA) Rights</h2>
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 my-4">
            <h3 className="text-lg font-semibold text-blue-400 mb-2">Your California Privacy Rights</h3>
            <p className="text-slate-300 mb-3">
              If you are a California resident, you have the following rights under the CCPA:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li><strong>Right to Know:</strong> Request disclosure of personal information we collect, use, and disclose</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (we do not sell your data)</li>
              <li><strong>Right to Non-Discrimination:</strong> Not receive discriminatory treatment for exercising your rights</li>
              <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
            </ul>
          </div>

          <h3 className="text-xl font-semibold mt-6 mb-3">Exercising Your Rights</h3>
          <p className="text-slate-300">
            To exercise your California privacy rights, you may:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Email us at cs@myrdkllc.com</li>
            <li>Use the account settings in our application</li>
            <li>Submit a request through our website</li>
          </ul>
          <p className="text-slate-300 mt-3">
            We will respond to verifiable requests within 45 days as required by the CCPA.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">5. Data Retention</h2>
          <p className="text-slate-300">
            We retain your personal information for as long as necessary to provide our Service 
            and fulfill the purposes described in this Privacy Policy. When you delete your account, 
            we will delete or anonymize your personal information within 30 days, except where we 
            are required to retain it for legal purposes.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">6. Data Security</h2>
          <p className="text-slate-300">
            We implement appropriate technical and organizational security measures to protect 
            your personal information, including:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments</li>
            <li>Access controls and authentication</li>
            <li>Secure data centers</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">7. Children's Privacy</h2>
          <p className="text-slate-300">
            Our Service is not intended for children under 13 years of age. We do not knowingly 
            collect personal information from children under 13. If we discover that we have 
            collected information from a child under 13, we will delete it promptly. If you 
            believe we may have information from a child under 13, please contact us.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">8. Cookies and Tracking</h2>
          <p className="text-slate-300">
            We use cookies and similar technologies to:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Keep you logged in</li>
            <li>Remember your preferences</li>
            <li>Analyze Service usage</li>
            <li>Improve user experience</li>
          </ul>
          <p className="text-slate-300 mt-3">
            You can control cookies through your browser settings. Disabling cookies may limit 
            your ability to use certain features of our Service.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">9. Third-Party Services</h2>
          <p className="text-slate-300">
            Our Service may contain links to third-party websites or services. We are not 
            responsible for the privacy practices of these third parties. We encourage you 
            to review their privacy policies.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">10. International Data Transfers</h2>
          <p className="text-slate-300">
            Your information may be transferred to and processed in countries other than your 
            country of residence. We ensure appropriate safeguards are in place for such transfers 
            in compliance with applicable laws.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">11. Changes to This Policy</h2>
          <p className="text-slate-300">
            We may update this Privacy Policy from time to time. We will notify you of material 
            changes by posting a notice on our Service or sending you an email. Your continued 
            use of the Service after changes indicates acceptance of the updated policy.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">12. Contact Us</h2>
          <p className="text-slate-300">
            For questions about this Privacy Policy or to exercise your privacy rights:
          </p>
          <div className="bg-slate-800 rounded-lg p-4 mt-4">
            <p className="text-slate-300">
              <strong>SlideCon (a DBA of RDK Services LLC)</strong><br /><br />
              <strong>Customer Service Email:</strong><br />
              cs@myrdkllc.com<br /><br />
              <strong>Mailing Address:</strong><br />
              15810 Gale Ave. Suite 285<br />
              Hacienda Heights, CA 91745<br />
              United States
            </p>
          </div>

          <h2 className="text-2xl font-semibold mt-8 mb-4">13. Do Not Track Signals</h2>
          <p className="text-slate-300">
            Some browsers have a "Do Not Track" feature that signals websites not to track your 
            online activities. Our Service currently does not respond to Do Not Track signals.
          </p>

          <div className="mt-12 pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              This Privacy Policy is effective as of January 13, 2026 and will remain in effect 
              except with respect to any changes in its provisions in the future.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
